import React, { useCallback, useContext, useEffect, useState } from "react";
import { CompPagination, Container } from "components";
import Breadcrumb from "components/molecules/Breadcrumb";
import { MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  API_URL_logDelete,
  API_URL_systemLog,
  base,
  baseurl,
} from "constants/api";
import { systemLogReducers } from "redux/systemLogSlice";
import { getData } from "actions";
import useDebounce from "hooks/useDebounce";
import { BiSortDown, BiSortUp } from "react-icons/bi";
import { SyncLoader } from "react-spinners";
import { FaExclamation, FaExclamationTriangle } from "react-icons/fa";
import { capitalFirstLetter } from "utils/capitalFirstLetter";
import moment from "moment";
import axios from "axios";
import { removeExtension } from "utils/removeExtension";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { AuthContext } from "context/AuthContext";

// field for use sort up/down
const TABLE_HEAD = [
  { title: "Level", field: "level" },
  { title: "Context", field: "context" },
  { title: "Date", field: "created_at" },
  { title: "Content", field: "" },
];

const Logs = () => {
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const currentDate = moment().format("YYYY-MM-DD");
  const currentLog = `${moment(currentDate).format("DD-MM-YYYY")}.log`;
  const dispatch = useDispatch();
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [fileLog, setFileLog] = useState([]);
  const [dateLog, setDateLog] = useState(currentDate);
  const [logActive, setLogActive] = useState(currentLog);
  const [queue, setqueue] = useState(false);

  const {
    getSystemLogResult,
    getSystemLogLoading,
    getSystemLogError,
    addSystemLogResult,
    deleteSystemLogResult,
  } = useSelector((state) => state.systemLog);

  const get = useCallback(
    async (params) => {
      getData(
        API_URL_systemLog,
        params,
        { dispatch, redux: systemLogReducers },
        "GET_SYSTEMLOG"
      );
    },
    [dispatch]
  );

  const handlePageClick = ({ selected }) => {
    const newOffset = selected * limit;
    const params = `?limit=${limit}&offset=${newOffset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    setOffset(newOffset);
    setCurrentPage(selected);
    get(params);
  };

  const delayedSearch = useCallback(
    useDebounce((value) => {
      //RESET PAGINATE KE 1
      setOffset(0);
      setCurrentPage(0);
      const params = `?limit=${limit}&offset=${offset}&search=${value}&ordering=${
        sortOrder === "desc" ? "-" : ""
      }${sortColumn}`;
      get(params);
    }, 1000),
    [limit, offset, sortOrder, sortColumn]
  );

  const onSearch = (value) => {
    setSearch(value);
    delayedSearch(value);
  };

  const handleSort = (column) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);
    if (column === sortColumn) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // Sort icons
  const renderSortIcon = (field) => {
    if (field === sortColumn) {
      return sortOrder === "asc" ? (
        <BiSortUp className="ml-1 h-4" />
      ) : (
        <BiSortDown className="ml-1 h-4" />
      );
    }
    return <BiSortUp className="ml-1 h-4 text-gray-300" />;
  };

  const handleSelectLimit = (limit) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);

    const params = `?limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
    setLimit(limit);
  };

  const handleDownloadLog = () => {
    window.location.href = base + "/media/logs/" + "queue.log";
  };

  const fetchData = useCallback(() => {
    const params = `?limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
  }, [get, limit, offset, sortColumn, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [limit, offset, sortColumn, sortOrder]);

  useEffect(() => {
    if (addSystemLogResult) {
      fetchData();
    }
  }, [addSystemLogResult, fetchData]);

  useEffect(() => {
    if (deleteSystemLogResult) {
      fetchData();
    }
  }, [deleteSystemLogResult, fetchData]);

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Logs"}
        excludedPaths={["administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          System Logs
          <div className="col-end-7 flex justify-end">
            <button
              onClick={handleDownloadLog}
              className="mr-1 p-1 px-3 rounded shadow-sm border text-white bg-sky-800 hover:bg-sky-700"
            >
              Download Log Queue
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row text-center md:justify-between align-middle px-4 my-4 bg-neutral dark:bg-neutral-800 ">
          <div className="p-1 inline-block align-middle">
            <select
              id="limit"
              value={limit}
              onChange={(e) => handleSelectLimit(e.target.value)}
              className="border rounded-sm p-1 border-gray-300 dark:text-black focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
            <span className="mx-2 text-sm ">Records per page</span>
          </div>
          <div className="p-1 inline-block align-middle">
            <span className="text-sm mr-2">Search:</span>
            <input
              id="search"
              type="text"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="by content"
              className="p-1 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>

        <div className="mx-4">
          <div className="overflow-x-auto grid grid-cols-12">
            <div className="col-span-12">
              <table className="border items-center w-full">
                <thead className="text-left">
                  <tr>
                    {TABLE_HEAD.map((item, itemIdx) => (
                      <th
                        key={itemIdx}
                        className="p-2 text-sm whitespace-nowrap cursor-pointer"
                        onClick={() => {
                          item.field && handleSort(item.field);
                        }}
                      >
                        <span className="flex">
                          {item.title}
                          {item.field && renderSortIcon(item.field)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* loading  */}
                  {getSystemLogLoading && (
                    <tr>
                      <td
                        className="text-center py-12"
                        colSpan={TABLE_HEAD.length + 1}
                      >
                        <div className="pt-10 pb-6 flex justify-center items-center">
                          <SyncLoader color="#4477CE" />
                        </div>
                      </td>
                    </tr>
                  )}
                  {/* Error */}
                  {getSystemLogError && (
                    <tr>
                      <td
                        className="text-center"
                        colSpan={TABLE_HEAD.length + 1}
                      >
                        <div className="pt-20 pb-12 flex justify-center items-center text-xs text-red-500">
                          {getSystemLogError}
                        </div>
                      </td>
                    </tr>
                  )}
                  {/* Result = 0 */}
                  {getSystemLogResult.results &&
                    queue === false &&
                    getSystemLogResult.results.length === 0 && (
                      <tr>
                        <td
                          className="text-center"
                          colSpan={TABLE_HEAD.length + 1}
                        >
                          <div className="pt-20 pb-12 flex justify-center items-center text-xs text-slate-600 dark:text-white">
                            No Data
                          </div>
                        </td>
                      </tr>
                    )}

                  {getSystemLogResult.results &&
                    getSystemLogResult.results.map((item, index) => (
                      <tr key={index}>
                        <td
                          className={`p-4 border border-blue-gray-50 
                            ${
                              item.level === "error"
                                ? "text-red-500 dark:text-red-400"
                                : item.level === "info"
                                ? "text-sky-500 dark:text-sky-400"
                                : "text-yellow-500 dark:text-yellow-400"
                            }`}
                        >
                          <div className="flex items-center">
                            <span className="mr-1">
                              {item.level === "error" ? (
                                <FaExclamationTriangle />
                              ) : item.level === "info" ? (
                                <FaExclamation />
                              ) : (
                                <FaExclamationTriangle />
                              )}
                            </span>
                            <span>{capitalFirstLetter(item.level)}</span>
                          </div>
                        </td>
                        <td className="p-4 border border-blue-gray-50">
                          {capitalFirstLetter(item.context)}
                        </td>
                        <td className="p-4 border border-blue-gray-50">
                          {moment(item.created_at).format(
                            "DD/MM/YYYY HH:mm:ss"
                          )}
                        </td>
                        <td className="p-4 border border-blue-gray-50">
                          {item.content && (
                            <span className="">{item.content}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pt-5 py-11">
            <CompPagination
              handlePageClick={(e) => handlePageClick(e)}
              pageCount={
                getSystemLogResult.count > 0 ? getSystemLogResult.count : 0
              }
              limit={limit}
              offset={offset}
              currentPage={currentPage}
            />
          </div>
        </div>
      </div>
    </Container>
  );
};

export default Logs;
